import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../services/analytics';

interface LocationCaptureProps {
  onLocationSet: (location: { city: string; latitude: number; longitude: number }) => void;
}

const LocationCapture: React.FC<LocationCaptureProps> = ({ onLocationSet }) => {
  const navigate = useNavigate();
  const [city, setCity] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);

  // Auto-detect location on component mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            // Reverse geocode to get city name
            const response = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
            );
            const data = await response.json();
            const detectedCity = data.city || data.locality || `${latitude.toFixed(2)}, ${longitude.toFixed(2)}`;
            setCity(detectedCity);
            setUseCurrentLocation(true);
          } catch (err) {
            console.warn('Could not detect city name:', err);
            setCity(`${latitude.toFixed(2)}, ${longitude.toFixed(2)}`);
            setUseCurrentLocation(true);
          }
        },
        (err) => {
          console.warn('Location detection failed:', err);
        }
      );
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!city.trim()) {
      setError('Please enter your location');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      let latitude, longitude;

      if (useCurrentLocation && navigator.geolocation) {
        // Use current GPS location
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        });
        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
      } else {
        // Geocode the city name
        const response = await fetch(
          `https://api.bigdatacloud.net/data/geocoding-client?query=${encodeURIComponent(city)}&localityLanguage=en`
        );
        const data = await response.json();
        
        if (!data.results || data.results.length === 0) {
          throw new Error('Location not found. Please try a different city name.');
        }
        
        latitude = data.results[0].latitude;
        longitude = data.results[0].longitude;
      }

      const locationData = {
        city: city.trim(),
        latitude,
        longitude
      };

      trackEvent('location_captured', JSON.stringify(locationData));
      onLocationSet(locationData);

    } catch (err: any) {
      setError(err.message || 'Could not find that location. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="location-capture">
      <style>{`
        .location-capture {
          min-height: 100vh;
          background: radial-gradient(circle at center, #0a0e23 0%, #000000 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          position: relative;
          overflow: hidden;
        }

        .location-form {
          background: rgba(30, 41, 59, 0.9);
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 16px;
          padding: 3rem;
          max-width: 500px;
          width: 100%;
          text-align: center;
          backdrop-filter: blur(10px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        }

        .location-title {
          font-size: 2rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 0.5rem;
          text-shadow: 0 0 20px rgba(139, 92, 246, 0.5);
        }

        .location-subtitle {
          font-size: 1.1rem;
          color: #94a3b8;
          margin-bottom: 2.5rem;
          line-height: 1.5;
        }

        .form-group {
          margin-bottom: 1.5rem;
          text-align: left;
        }

        .form-label {
          display: block;
          font-size: 0.9rem;
          font-weight: 600;
          color: #e2e8f0;
          margin-bottom: 0.5rem;
        }

        .form-input {
          width: 100%;
          padding: 1rem;
          border: 1px solid rgba(148, 163, 184, 0.3);
          border-radius: 8px;
          background: rgba(15, 23, 42, 0.8);
          color: #ffffff;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .form-input:focus {
          outline: none;
          border-color: #8b5cf6;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }

        .location-option {
          display: flex;
          align-items: center;
          margin-bottom: 1rem;
          padding: 0.75rem;
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 8px;
          background: rgba(15, 23, 42, 0.5);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .location-option:hover {
          border-color: #8b5cf6;
          background: rgba(139, 92, 246, 0.1);
        }

        .location-option.active {
          border-color: #8b5cf6;
          background: rgba(139, 92, 246, 0.2);
        }

        .location-radio {
          margin-right: 0.75rem;
        }

        .location-option-text {
          color: #e2e8f0;
          font-size: 0.9rem;
        }

        .continue-button {
          width: 100%;
          padding: 1rem 2rem;
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
          color: #ffffff;
          border: none;
          border-radius: 12px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .continue-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(139, 92, 246, 0.4);
        }

        .continue-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .error-message {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 8px;
          padding: 0.75rem;
          color: #fca5a5;
          font-size: 0.9rem;
          margin-top: 1rem;
        }

        .loading-spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: #ffffff;
          animation: spin 1s ease-in-out infinite;
          margin-right: 0.5rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .stars-container {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: -1;
        }

        .star {
          position: absolute;
          background: #ffffff;
          border-radius: 50%;
          opacity: 0.4;
          animation: twinkle 3s ease-in-out infinite;
        }

        @keyframes twinkle {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
      `}</style>

      <div className="stars-container">
        {Array.from({ length: 100 }).map((_, i) => (
          <div
            key={i}
            className="star"
            style={{
              width: `${Math.random() * 2 + 1}px`,
              height: `${Math.random() * 2 + 1}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
            }}
          />
        ))}
      </div>

      <div className="location-form">
        <h1 className="location-title">🌍 Set Your Location</h1>
        <p className="location-subtitle">
          We need your location to show you the best satellite passes and launch viewing opportunities in your area.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label
              className={`location-option ${useCurrentLocation ? 'active' : ''}`}
              onClick={() => setUseCurrentLocation(true)}
            >
              <input
                type="radio"
                className="location-radio"
                checked={useCurrentLocation}
                onChange={() => setUseCurrentLocation(true)}
              />
              <div className="location-option-text">
                📍 Use my current location {city && `(${city})`}
              </div>
            </label>

            <label
              className={`location-option ${!useCurrentLocation ? 'active' : ''}`}
              onClick={() => setUseCurrentLocation(false)}
            >
              <input
                type="radio"
                className="location-radio"
                checked={!useCurrentLocation}
                onChange={() => setUseCurrentLocation(false)}
              />
              <div className="location-option-text">
                🏙️ Enter city manually
              </div>
            </label>
          </div>

          {!useCurrentLocation && (
            <div className="form-group">
              <label htmlFor="city" className="form-label">
                City or Address
              </label>
              <input
                type="text"
                id="city"
                className="form-input"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g., New York, London, Tokyo"
                required
              />
            </div>
          )}

          <button
            type="submit"
            className="continue-button"
            disabled={isLoading || !city.trim()}
          >
            {isLoading && <span className="loading-spinner" />}
            {isLoading ? 'Finding Location...' : 'Continue to Payment →'}
          </button>

          {error && <div className="error-message">{error}</div>}
        </form>
      </div>
    </div>
  );
};

export default LocationCapture;